import type { RouteLocationNormalized } from 'vue-router'
import { useAuthenticationStore } from '@cplug/frontend-shared-domains/domain/authentication'
import { defineNuxtRouteMiddleware, navigateTo } from '#app'
import { restoreSessionOnce } from '~/domain/shared/authentication/composables/useSessionRestoreState'
import { resolveMissingPermissions, useProfileHasPermission } from '~/domain/dashboard/user/logged-user/composables/useProfileHasPermission'
import { resolveRoutePermissionRule } from '~/domain/dashboard/user/logged-user/composables/useRoutePermissionMap'
import useLoggedUserStore from '~/domain/dashboard/user/logged-user/stores/useLoggedUserStore'
import { FORBIDDEN_ACCESS_PAGE_PATH } from '~/shared/models/ForbiddenAccess'
import useForbiddenAccessStore from '~/shared/stores/useForbiddenAccessStore'

const PERMISSION_ALLOWLIST_EXACT = new Set([
  '/login',
  '/company',
  '/terms',
  FORBIDDEN_ACCESS_PAGE_PATH,
  '/dashboard/blocked-plan',
  '/dashboard/overdue-payment',
  '/dashboard/maintenance'
])

const PERMISSION_ALLOWLIST_PREFIX = ['/auth']

function isPermissionAllowlisted (path: string): boolean {
  if (PERMISSION_ALLOWLIST_EXACT.has(path)) {
    return true
  }

  return PERMISSION_ALLOWLIST_PREFIX.some(prefix => path === prefix || path.startsWith(`${prefix}/`))
}

function redirectToForbiddenAccess (to: RouteLocationNormalized, permissionSet: Set<string>): ReturnType<typeof navigateTo> {
  const routeRule = resolveRoutePermissionRule(to.path)

  if (routeRule != null) {
    const forbiddenAccessStore = useForbiddenAccessStore()
    forbiddenAccessStore.addMissingPermissions(
      resolveMissingPermissions(routeRule, permissionSet),
      { blockedFrom: to.fullPath, blockedApiUrl: null, blockedApiMethod: null }
    )
  }

  return navigateTo(FORBIDDEN_ACCESS_PAGE_PATH, { replace: true })
}

export default defineNuxtRouteMiddleware(async (to: RouteLocationNormalized) => {
  if (!to.path.startsWith('/dashboard')) {
    return
  }

  if (isPermissionAllowlisted(to.path)) {
    return
  }

  await restoreSessionOnce()

  const authStore = useAuthenticationStore()
  if (!authStore.logged) {
    return
  }

  const loggedUserStore = useLoggedUserStore()
  await loggedUserStore.getPermissions()

  const routeRule = resolveRoutePermissionRule(to.path)
  const permissionsFetchFailed = loggedUserStore.permissionErrors.length > 0

  if (permissionsFetchFailed && routeRule != null) {
    return redirectToForbiddenAccess(to, loggedUserStore.permissionSet)
  }

  const { canAccessRoute } = useProfileHasPermission()
  if (!canAccessRoute(to.path)) {
    return redirectToForbiddenAccess(to, loggedUserStore.permissionSet)
  }
})
